import type { Command } from "../document/commands.ts";
import type { LayerDocument, MapDocument, TileRef, WorldDocument } from "../document/types.ts";
import { fillStart, planFill, type FillOutcome, type FillPlan, type FillValue } from "./fill.ts";

/**
 * Fill (P1.6-T40): the region under a point on the active layer filled as one command, and the fill over
 * `FILL_ASK_TILES` that waits to be confirmed. The waiting fill is view state, dropped as soon as its layer
 * changes, locks or hides.
 */

/**
 * What fill needs from the store: the layers it reads and what each kind of layer is filled with.
 */
export interface FillHost {
  readonly document: () => WorldDocument;
  readonly map: () => MapDocument;
  readonly activeLayerId: () => string | null;
  readonly isEditable: (layerId: string) => boolean;
  readonly isLocked: (layerId: string) => boolean;
  readonly layerStands: (mapId: string, layer: LayerDocument) => boolean;
  readonly tileInHand: () => TileRef | null;
  readonly zoneType: () => string | undefined;
  readonly activeColour: () => number;
  readonly collisionValue: () => number | undefined;
  readonly run: (command: Command) => boolean;
  readonly notify: () => void;
}

/**
 * What callers reach of fill, through `EditorStore.fill`.
 */
export type FillSlice = Pick<FillAuthoring, "at" | "pending" | "confirmed" | "confirm" | "cancel">;

/**
 * The filler.
 */
export class FillAuthoring {
  private waiting: { readonly plan: FillPlan; readonly mapId: string; readonly layer: LayerDocument } | null = null;
  private confirmedFills = 0;

  /**
   * @param host The store's view of itself.
   */
  public constructor(private readonly host: FillHost) {}

  /**
   * Fills the region under a point on the active layer (P1.6-T40): the 4-connected cells of the layer's grid
   * holding what the cell under the point holds, bounded by the map, take the colour of the active terrain
   * layer, the tile in hand or the collision brush's value, or are cleared with `erase`, as one command. A
   * terrain fill resolves its tiles as a stroke over the same vertices does. A fill over `FILL_ASK_TILES`
   * records nothing and waits as `pending` for `confirm` or `cancel`. A locked or hidden layer
   * takes none of it.
   *
   * @param x The point's x, in tiles.
   * @param y The point's y, in tiles.
   * @param erase True to clear the region instead.
   * @returns What the fill did.
   */
  public at(x: number, y: number, erase: boolean): FillOutcome {
    const layerId = this.host.activeLayerId();
    const map = this.host.map();
    const layer = map.layers.find((l) => l.id === layerId);
    if (layerId === null || layer === undefined || layer.kind === "prop" || layer.kind === "entity" || layer.kind === "door") {
      return { kind: "unsupported" };
    }
    if (!this.host.isEditable(layerId)) {
      return { kind: "refused", why: this.host.isLocked(layerId) ? "locked" : "hidden" };
    }
    const start = fillStart(map, layerId, x, y);
    if (start === null) {
      return { kind: "off map" };
    }
    const value = this.valueFor(layer.kind, erase);
    if (value === undefined) {
      return { kind: "empty hand" };
    }
    const plan = planFill(map, layerId, start, value, this.host.document().tilesets);
    if (plan === null) {
      return { kind: "unchanged" };
    }
    if (plan.asks) {
      this.waiting = { plan, mapId: map.id, layer };
      this.host.notify();
      return { kind: "asks", tiles: plan.tiles };
    }
    return this.host.run(plan.command) ? { kind: "filled", cells: plan.cells } : { kind: "unchanged" };
  }

  /**
   * The fill waiting to be confirmed because it is over the limit (P1.6-T40). View state: never saved, and
   * dropped as soon as its layer changes, locks or hides, so a confirmed fill always applies to the layer it
   * was planned on. A change elsewhere in the world (another builder's edit to another layer or map) leaves
   * it waiting: the plan reads its own layer and the tileset list only.
   *
   * @returns The plan, or null.
   */
  public get pending(): FillPlan | null {
    return this.waiting?.plan ?? null;
  }

  /**
   * How many pending fills were confirmed and recorded: what a host reads to tell a fill that ran from one
   * that was cancelled, refused or dropped.
   *
   * @returns The count since the store was made.
   */
  public get confirmed(): number {
    return this.confirmedFills;
  }

  /**
   * Runs the pending fill as one command. The fill is counted before the command runs, since the command's
   * own notification is where a host reads the count to say the fill was made, and the count is taken back
   * when the command is refused.
   *
   * @returns True when it was recorded; false with no pending fill, one whose layer has changed since it
   *   was planned, or one a lock, an eye or the session refused.
   */
  public confirm(): boolean {
    const waiting = this.waiting;
    if (waiting === null) {
      return false;
    }
    this.waiting = null;
    if (waiting.mapId !== this.host.map().id || !this.host.layerStands(waiting.mapId, waiting.layer)) {
      this.host.notify();
      return false;
    }
    this.confirmedFills += 1;
    if (!this.host.run(waiting.plan.command)) {
      this.confirmedFills -= 1;
      this.host.notify();
      return false;
    }
    return true;
  }

  /**
   * Drops the pending fill unapplied.
   */
  public cancel(): void {
    if (this.waiting !== null) {
      this.waiting = null;
      this.host.notify();
    }
  }

  /**
   * Drops the waiting fill, telling no one.
   */
  public drop(): void {
    this.waiting = null;
  }

  /**
   * Drops the waiting fill when the map in view no longer holds its layer as planned, or the layer is locked
   * or hidden, telling no one.
   */
  public release(): void {
    const waiting = this.waiting;
    if (waiting !== null && (waiting.mapId !== this.host.map().id || !this.host.layerStands(waiting.mapId, waiting.layer) || !this.host.isEditable(waiting.plan.layerId))) {
      this.waiting = null;
    }
  }

  /**
   * What a fill writes on a layer kind.
   *
   * @param kind The active layer's kind.
   * @param erase True to clear.
   * @returns The value, or undefined with nothing in hand.
   */
  private valueFor(kind: "terrain" | "tile" | "collision" | "zone", erase: boolean): FillValue | undefined {
    if (kind === "tile") {
      return erase ? null : (this.host.tileInHand() ?? undefined);
    }
    if (kind === "zone") {
      return erase ? null : this.host.zoneType();
    }
    if (erase) {
      return 0;
    }
    return kind === "terrain" ? this.host.activeColour() : this.host.collisionValue();
  }
}
